import {
    ElementHelper,
    getDatasheetProperty,
    getDefaultPropertyValueWithUnit,
    getManufacturerPartNumberProperty,
    systemDefaultRulesSuffix,
} from "@buildwithflux/core";
import type {
    AnyPcbBakedNode,
    AnyPcbNode,
    IDocumentData,
    IElementData,
    INetsMap,
    NodeUid,
    PcbNodesMap,
    VertexUid,
} from "@buildwithflux/models";
import {LayoutRules} from "@buildwithflux/models";
import {createSelector} from "reselect";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";
import {emptyArray} from "../utils";

/**
 * Derived selectors for selection - computed values
 * These selectors filter and compute selection data based on object types
 * Note: These are expensive computations that are memoized
 */

/** Empty array constant for vertex UIDs to avoid creating new arrays on every render */
const emptyVertexUidsArray: VertexUid[] = [];

/** Empty array constant for elements to avoid creating new arrays on every render */
const emptyElementsArray: IElementData[] = [];

/** Gets selected element UIDs (filtered, excludes branch points) - memoized */
export const elementUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elements): string[] => {
        if (!elements) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            const element = elements[uid];
            if (element && !ElementHelper.isBranchPointElement(element)) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets selected elements (full data, excludes branch points) - memoized */
export const elements: DocumentSelector<IElementData[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elementsMap): IElementData[] => {
        if (!elementsMap) return emptyElementsArray;
        const result: IElementData[] = [];
        for (const uid of selectedUids) {
            const element = elementsMap[uid];
            if (element && !ElementHelper.isBranchPointElement(element)) {
                result.push(element);
            }
        }
        return result;
    },
);

/** Gets selected symbol UIDs - memoized */
export const symbolUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elements): string[] => {
        if (!elements) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            const elementUid = ElementHelper.parseSymbolUid(uid)?.elementUid;
            if (elementUid && elements[elementUid]) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets selected net UIDs - memoized */
export const netUids: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray, (state: DocumentStoreState) => state.nets],
    (selectedUids, nets): string[] => {
        if (!nets) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            if (nets[uid]) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets selected nets (full data) - memoized */
export const nets: DocumentSelector<Array<INetsMap[string]>> = createSelector(
    [(state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray, (state: DocumentStoreState) => state.nets],
    (selectedUids, netsMap): Array<INetsMap[string]> => {
        if (!netsMap) return [];
        const result: Array<INetsMap[string]> = [];
        for (const uid of selectedUids) {
            const net = netsMap[uid];
            if (net) {
                result.push(net);
            }
        }
        return result;
    },
);

/** Gets selected vertex UIDs - memoized */
export const vertexUids: DocumentSelector<VertexUid[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.routeVertices,
    ],
    (selectedUids, routeVertices): VertexUid[] => {
        if (!routeVertices) return emptyVertexUidsArray;
        const result: VertexUid[] = [];
        for (const uid of selectedUids) {
            if (routeVertices[uid]) {
                result.push(uid as VertexUid);
            }
        }
        return result;
    },
);

/** Gets selected route UIDs - memoized */
export const routeUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.routes,
    ],
    (selectedUids, routes): string[] => {
        if (!routes) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            if (routes[uid]) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets selected comment thread UIDs - memoized */
export const commentThreadUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.comment_threads,
    ],
    (selectedUids, commentThreads): string[] => {
        if (!commentThreads) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            if (commentThreads[uid]) {
                result.push(uid);
            }
        }
        return result;
    },
);

/**
 * Gets selected PCB layout node UIDs from document.pcbLayoutNodes only (UNBAKED nodes).
 * Does NOT include baked nodes from documentService.snapshot().pcbLayoutNodes
 * (e.g. silk text inside components).
 *
 * Use this only when you explicitly want to ignore baked nodes and operate
 * on unbaked/store layout nodes. For most selection-dependent UI (bounding boxes,
 * context menus, delete, etc.), use useAllSelectedPcbLayoutNodeUids hook instead.
 */
export const unbakedPcbLayoutNodeUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.pcbLayoutNodes,
    ],
    (selectedUids, pcbLayoutNodes): string[] => {
        if (!pcbLayoutNodes) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            if (pcbLayoutNodes[uid]) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets selected PCB layout rule set UIDs - memoized
 * Checks both user rulesets (in pcbLayoutRuleSets) AND system rulesets
 * (identified by UID containing systemDefaultRulesSuffix)
 */
export const pcbLayoutRuleSetUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.pcbLayoutRuleSets,
    ],
    (selectedUids, pcbLayoutRuleSets): string[] => {
        const result: string[] = [];
        for (const uid of selectedUids) {
            // Check if it's a user ruleset OR a system ruleset
            if (pcbLayoutRuleSets?.[uid] || uid.includes(systemDefaultRulesSuffix)) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets selected locked element UIDs - memoized */
export const lockedElementUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elements): string[] => {
        if (!elements) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            const element = elements[uid];
            if (element?.locked) {
                result.push(uid);
            }
        }
        return result;
    },
);

/** Gets selected element and net UIDs (excludes branch points) - memoized */
export const elementAndNetUids: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
        (state: DocumentStoreState) => state.nets,
    ],
    (selectedUids, elements, nets): string[] => {
        if (!elements && !nets) return emptyArray;
        const result: string[] = [];
        for (const uid of selectedUids) {
            const element = elements?.[uid];
            const net = nets?.[uid];
            if ((element && !ElementHelper.isBranchPointElement(element)) || net) {
                result.push(uid);
            }
        }
        return result;
    },
);

/**
 * Count selectors - return primitive numbers to avoid useShallow overhead
 * These are optimized for components that only need to check counts or existence
 */

/** Gets count of selected element UIDs (filtered, excludes branch points) - memoized */
export const elementUidsCount: DocumentSelector<number> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elements): number => {
        if (!elements) return 0;
        let count = 0;
        for (const uid of selectedUids) {
            const element = elements[uid];
            if (element && !ElementHelper.isBranchPointElement(element)) {
                count++;
            }
        }
        return count;
    },
);

/** Gets count of selected locked element UIDs (excludes branch points, consistent with elementUidsCount) - memoized */
export const lockedElementUidsCount: DocumentSelector<number> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elements): number => {
        if (!elements) return 0;
        let count = 0;
        for (const uid of selectedUids) {
            const element = elements[uid];
            if (element && !ElementHelper.isBranchPointElement(element) && element.locked) {
                count++;
            }
        }
        return count;
    },
);

/** Gets count of selected symbol UIDs - memoized */
export const symbolUidsCount: DocumentSelector<number> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elements): number => {
        if (!elements) return 0;
        let count = 0;
        for (const uid of selectedUids) {
            const elementUid = ElementHelper.parseSymbolUid(uid)?.elementUid;
            if (elementUid && elements[elementUid]) {
                count++;
            }
        }
        return count;
    },
);

/** Gets count of selected net UIDs - memoized */
export const netUidsCount: DocumentSelector<number> = createSelector(
    [(state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray, (state: DocumentStoreState) => state.nets],
    (selectedUids, nets): number => {
        if (!nets) return 0;
        let count = 0;
        for (const uid of selectedUids) {
            if (nets[uid]) {
                count++;
            }
        }
        return count;
    },
);

/** Gets total count of all selected object UIDs */
export const allCount: DocumentSelector<number> = (state: DocumentStoreState) => state.selectedObjectUids?.length ?? 0;

/** Gets count of selected unbaked PCB layout node UIDs - memoized */
export const unbakedPcbLayoutNodeUidsCount: DocumentSelector<number> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.pcbLayoutNodes,
    ],
    (selectedUids, pcbLayoutNodes): number => {
        if (!pcbLayoutNodes) return 0;
        let count = 0;
        for (const uid of selectedUids) {
            if (pcbLayoutNodes[uid]) {
                count++;
            }
        }
        return count;
    },
);

/** Gets count of selected PCB layout rule set UIDs - memoized */
export const pcbLayoutRuleSetUidsCount: DocumentSelector<number> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.pcbLayoutRuleSets,
    ],
    (selectedUids, pcbLayoutRuleSets): number => {
        let count = 0;
        for (const uid of selectedUids) {
            if (pcbLayoutRuleSets?.[uid] || uid.includes(systemDefaultRulesSuffix)) {
                count++;
            }
        }
        return count;
    },
);

/** Gets count of selected route UIDs - memoized */
export const routeUidsCount: DocumentSelector<number> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.routes,
    ],
    (selectedUids, routes): number => {
        if (!routes) return 0;
        let count = 0;
        for (const uid of selectedUids) {
            if (routes[uid]) {
                count++;
            }
        }
        return count;
    },
);

/** Gets the single selected element UID when exactly one element is selected, undefined otherwise */
export const singleSelectedElementUid: DocumentSelector<string | undefined> = createSelector(
    [elementUidsCount, elementUids],
    (count, uids): string | undefined => {
        return count === 1 ? uids[0] : undefined;
    },
);

/** Gets the single selected symbol UID when exactly one symbol is selected, undefined otherwise */
export const singleSelectedSymbolUid: DocumentSelector<string | undefined> = createSelector(
    [symbolUidsCount, symbolUids],
    (count, uids): string | undefined => {
        return count === 1 ? uids[0] : undefined;
    },
);

/**
 * Checks if only elements (and their symbols) are selected - no nets, routes, pcb nodes, etc.
 * Returns a boolean to avoid useShallow overhead.
 */
export const onlyElementsSelected: DocumentSelector<boolean> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
    ],
    (selectedUids, elements): boolean => {
        if (selectedUids.length === 0 || !elements) return false;
        for (const uid of selectedUids) {
            const element = elements[uid];
            if (element) continue;
            const elementUid = ElementHelper.parseSymbolUid(uid)?.elementUid;
            if (elementUid && elements[elementUid]) continue;
            return false;
        }
        return true;
    },
);

/**
 * Checks if any selected UID is "unknown" — i.e., not found in elements, nets, routeVertices,
 * routes, comment threads, PCB layout nodes, or PCB layout rule sets.
 * Returns true if ALL UIDs are recognized, false if any is unknown.
 * This is used in SubjectPanels to decide whether to show the multi-selection panel.
 *
 * NOTE: This only checks unbaked PCB nodes (from the store). Scene-level subjects like
 * route segments and symbols are checked separately in the component.
 */
export const hasUnknownSelectionUid: DocumentSelector<boolean> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
        (state: DocumentStoreState) => state.nets,
        (state: DocumentStoreState) => state.routeVertices,
        (state: DocumentStoreState) => state.routes,
        (state: DocumentStoreState) => state.comment_threads,
        (state: DocumentStoreState) => state.pcbLayoutNodes,
        (state: DocumentStoreState) => state.pcbLayoutRuleSets,
    ],
    (
        selectedUids,
        elements,
        nets,
        routeVertices,
        routes,
        commentThreads,
        pcbLayoutNodes,
        pcbLayoutRuleSets,
    ): boolean => {
        for (const uid of selectedUids) {
            if (elements?.[uid]) continue;
            if (nets?.[uid]) continue;
            const elementUid = ElementHelper.parseSymbolUid(uid)?.elementUid;
            if (elementUid && elements?.[elementUid]) continue;
            if (routeVertices?.[uid]) continue;
            if (routes?.[uid]) continue;
            if (commentThreads?.[uid]) continue;
            if (pcbLayoutNodes?.[uid]) continue;
            if (pcbLayoutRuleSets?.[uid] || uid.includes(systemDefaultRulesSuffix)) continue;
            return true;
        }
        return false;
    },
);

/**
 * Checks if any selected element lacks a Manufacturer Part Number property value.
 * Returns a boolean primitive to avoid useShallow overhead.
 * Used by CopilotMenuItems to reactively enable/disable "Recommend MPN" menu item.
 */
export const hasSelectedElementWithoutMpn: DocumentSelector<boolean> = createSelector(
    [elementUids, (state: DocumentStoreState) => state.elements],
    (selectedElementUids, elements): boolean => {
        if (!elements) return false;
        for (const uid of selectedElementUids) {
            const element = elements[uid];
            if (element && !getManufacturerPartNumberProperty(element.properties)?.value) {
                return true;
            }
        }
        return false;
    },
);

/**
 * Pure function to check whether a single UID is recognized in any store-level collection.
 * Mirrors the same checks as `hasUnknownSelectionUid` but for a single UID.
 * Used by SubjectPanels to combine store-level and scene-level unknown checks
 * without pulling in full reactive collections.
 */
export function computeIsUidKnownInStore(
    uid: string,
    elements: DocumentStoreState["elements"],
    nets: DocumentStoreState["nets"],
    routeVertices: DocumentStoreState["routeVertices"],
    routes: DocumentStoreState["routes"],
    commentThreads: DocumentStoreState["comment_threads"],
    pcbLayoutNodes: DocumentStoreState["pcbLayoutNodes"],
    pcbLayoutRuleSets: DocumentStoreState["pcbLayoutRuleSets"],
): boolean {
    if (elements?.[uid]) return true;
    if (nets?.[uid]) return true;
    const elementUid = ElementHelper.parseSymbolUid(uid)?.elementUid;
    if (elementUid && elements?.[elementUid]) return true;
    if (routeVertices?.[uid]) return true;
    if (routes?.[uid]) return true;
    if (commentThreads?.[uid]) return true;
    if (pcbLayoutNodes?.[uid]) return true;
    if (pcbLayoutRuleSets?.[uid] || uid.includes(systemDefaultRulesSuffix)) return true;
    return false;
}

/** Gets labels (designators) for selected objects across elements, nets, and pcbLayoutNodes - memoized */
export const selectedDesignators: DocumentSelector<string[]> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
        (state: DocumentStoreState) => state.nets,
        (state: DocumentStoreState) => state.pcbLayoutNodes,
    ],
    (selectedUids, elements, nets, pcbLayoutNodes): string[] => {
        const result: string[] = [];
        for (const uid of selectedUids) {
            const label = elements?.[uid]?.label || nets?.[uid]?.label || pcbLayoutNodes?.[uid]?.name;
            if (label) {
                result.push(label);
            }
        }
        return result;
    },
);

/** Gets selected elements that have a datasheet property value - memoized */
export const elementsWithDatasheet: DocumentSelector<IElementData[]> = createSelector(
    [elements],
    (selectedElements): IElementData[] => {
        const result: IElementData[] = [];
        for (const element of selectedElements) {
            if (getDatasheetProperty(element.properties)?.value) {
                result.push(element);
            }
        }
        return result;
    },
);

/** Gets selected elements that do NOT have a datasheet property value - memoized */
export const elementsWithoutDatasheet: DocumentSelector<IElementData[]> = createSelector(
    [elements],
    (selectedElements): IElementData[] => {
        const result: IElementData[] = [];
        for (const element of selectedElements) {
            if (!getDatasheetProperty(element.properties)?.value) {
                result.push(element);
            }
        }
        return result;
    },
);

/** Gets selected elements that have a label AND a default property value with unit - memoized */
export const elementsWithDefaultValue: DocumentSelector<IElementData[]> = createSelector(
    [elements],
    (selectedElements): IElementData[] => {
        const result: IElementData[] = [];
        for (const element of selectedElements) {
            if (element.label && getDefaultPropertyValueWithUnit(element.properties)) {
                result.push(element);
            }
        }
        return result;
    },
);

/**
 * Gets the shared label for multi-selection, returning "Mixed" if labels differ.
 * Returns undefined if no objects are selected or document is null.
 * Checks across elements, nets, and pcbLayoutNodes.
 */
export const multiSelectionLabel: DocumentSelector<string | undefined> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
        (state: DocumentStoreState) => state.nets,
        (state: DocumentStoreState) => state.pcbLayoutNodes,
    ],
    (selectedUids, elements, nets, pcbLayoutNodes): string | undefined => {
        let prevLabel: string | undefined;
        for (const uid of selectedUids) {
            const currentLabel = elements?.[uid]?.label ?? nets?.[uid]?.label ?? pcbLayoutNodes?.[uid]?.name;
            if (prevLabel === undefined) {
                prevLabel = currentLabel;
            } else if (prevLabel !== currentLabel) {
                return "Mixed";
            }
        }
        return prevLabel;
    },
);

/**
 * Checks if a PCB node is locked by inheritance (any ancestor is locked).
 * Local helper to avoid circular imports with hooks/pcbLayoutNodes.ts.
 */
function isNodeLockedByInheritance(
    pcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined,
    nodeUid: string | undefined,
): boolean {
    if (!nodeUid || !pcbLayoutNodes) return false;
    const node = pcbLayoutNodes[nodeUid];
    if (!node) return false;
    if (node.locked) return true;
    if (!node.parentUid) return false;
    return isNodeLockedByInheritance(pcbLayoutNodes, node.parentUid);
}

/** Checks if any selected object is locked (elements or PCB nodes, including inherited locks) - memoized */
export const anySelectedObjectLocked: DocumentSelector<boolean> = createSelector(
    [
        (state: DocumentStoreState) => state.selectedObjectUids ?? emptyArray,
        (state: DocumentStoreState) => state.elements,
        (state: DocumentStoreState) => state.pcbLayoutNodes,
    ],
    (selectedUids, elements, pcbLayoutNodes): boolean => {
        for (const uid of selectedUids) {
            if (elements?.[uid]?.locked) return true;
            const node = pcbLayoutNodes?.[uid];
            if (node?.locked) return true;
            if (node && isNodeLockedByInheritance(pcbLayoutNodes, uid)) return true;
        }
        return false;
    },
);

/**
 * Factory selector: checks if a specific object (element or PCB node) is locked.
 * Checks direct lock on elements, direct lock on PCB nodes, and inherited lock for PCB nodes.
 */
export const isObjectLocked: ParameterizedSelector<string | undefined, boolean> = (uid) => (state) => {
    if (!uid) return false;
    const element = state.elements?.[uid];
    if (element?.locked) return true;
    const node = state.pcbLayoutNodes?.[uid];
    if (node?.locked) return true;
    if (node) return isNodeLockedByInheritance(state.pcbLayoutNodes, uid);
    return false;
};

// ============================================================================
// Pure compute functions (not selectors - take parameters directly)
// These are for non-reactive access in event handlers and hotkey callbacks
// ============================================================================

/**
 * Pure function to compute selected locked element UIDs from document data.
 * This is used for non-reactive access in event handlers and hotkey callbacks.
 * @param document The document data
 * @returns Array of locked element UIDs that are selected
 */
export function computeSelectedLockedElementUids(document: IDocumentData | null | undefined): string[] {
    if (!document) {
        return [];
    }

    const selectedObjectUids = document.selectedObjectUids || [];
    const elements = document.elements;
    if (!elements) {
        return [];
    }

    const result: string[] = [];
    for (const uid of selectedObjectUids) {
        const element = elements[uid];
        if (element?.locked) {
            result.push(uid);
        }
    }
    return result;
}

/**
 * Pure function to check if selected PCB nodes support rotation.
 * This checks if any selected node has a type that supports rotation.
 * @param pcbLayoutNodes The PCB layout nodes map (should include baked nodes from documentService.snapshot().pcbLayoutNodes)
 * @param selectedObjectUids The selected object UIDs
 * @returns True if any selected PCB node supports rotation
 */
export function computeSelectedPcbNodesSupportRotation(
    pcbLayoutNodes: PcbNodesMap<AnyPcbNode> | undefined,
    selectedObjectUids: string[],
): boolean {
    const unsupportedTypes = LayoutRules.rotation.unsupportedNodeTypes ?? [];

    for (const uid of selectedObjectUids) {
        const node = pcbLayoutNodes?.[uid];
        if (node && !unsupportedTypes.includes(node.type)) {
            return true;
        }
    }
    return false;
}

/**
 * Pure function to compute the single selected PCB node UID.
 * Returns undefined if no PCB nodes are selected or if multiple are selected.
 * This function checks both baked and unbaked nodes.
 * @param selectedObjectUids The selected object UIDs from the document
 * @param allPcbNodes All baked PCB nodes (from getAllBakedNodes)
 */
export function computeSingleSelectedPcbNodeUid(
    selectedObjectUids: string[] | undefined,
    allPcbNodes: {[key: string]: unknown},
): string | undefined {
    if (!selectedObjectUids || selectedObjectUids.length !== 1) return undefined;

    const selectedUid = selectedObjectUids[0];
    if (!selectedUid) return undefined;

    // Check if the selected UID is a PCB node
    if (selectedUid in allPcbNodes) {
        return selectedUid;
    }

    return undefined;
}

/**
 * Pure function to compute selected elements from document state.
 * Returns an array of selected element data, filtering out branch points.
 * This matches the behavior of the Redux selectSelectedElements selector.
 */
export function computeSelectedElements(document: IDocumentData | null | undefined): IElementData[] {
    if (!document) return emptyElementsArray;

    const selectedObjectUids = document.selectedObjectUids || emptyArray;
    const elements = document.elements;

    if (!elements) return emptyElementsArray;

    const selectedElements: IElementData[] = [];
    for (const uid of selectedObjectUids) {
        const element = elements[uid];
        if (element && !ElementHelper.isBranchPointElement(element)) {
            selectedElements.push(element);
        }
    }

    return selectedElements;
}

/**
 * Pure function to compute non-branch point elements from document state.
 * Returns an array of all elements that are not branch points.
 * This is useful for callbacks that need non-reactive access to elements.
 */
export function computeNonBranchPointElements(document: IDocumentData | null | undefined): IElementData[] {
    if (!document?.elements) return emptyElementsArray;

    const result: IElementData[] = [];
    for (const element of Object.values(document.elements)) {
        if (!ElementHelper.isBranchPointElement(element)) {
            result.push(element);
        }
    }
    return result;
}

/**
 * Pure function to compute all selected PCB layout node UIDs, checking both
 * baked nodes (from documentService.snapshot().pcbLayoutNodes, e.g. silk text inside components)
 * and unbaked nodes (from document.pcbLayoutNodes).
 *
 * Use this for selection-dependent UI (bounding boxes, context menus, delete, etc.)
 * where baked nodes like silk text must be handled.
 *
 * @param selectedObjectUids The selected object UIDs from the document
 * @param allBakedNodes All baked PCB nodes (from getAllBakedNodes)
 * @param unbakedPcbLayoutNodes The unbaked PCB layout nodes from the document store
 * @returns Array of selected PCB layout node UIDs
 */
export function computeAllSelectedPcbLayoutNodeUids(
    selectedObjectUids: string[] | undefined,
    allBakedNodes: {[key: string]: unknown},
    unbakedPcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined,
): NodeUid[] {
    if (!selectedObjectUids) return [];

    const result: NodeUid[] = [];
    for (const uid of selectedObjectUids) {
        if (uid in allBakedNodes || (unbakedPcbLayoutNodes && uid in unbakedPcbLayoutNodes)) {
            result.push(uid);
        }
    }
    return result;
}

/**
 * Pure function to compute count of all selected PCB layout node UIDs, checking both
 * baked nodes (from documentService.snapshot().pcbLayoutNodes, e.g. silk text inside components)
 * and unbaked nodes (from document.pcbLayoutNodes).
 *
 * Use this for selection-dependent UI when you only need the count (e.g. menu enable/disable)
 * and want to avoid allocating an array.
 */
export function computeAllSelectedPcbLayoutNodeUidsCount(
    selectedObjectUids: string[] | undefined,
    allBakedNodes: {[key: string]: unknown},
    unbakedPcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined,
): number {
    if (!selectedObjectUids) return 0;

    let count = 0;
    for (const uid of selectedObjectUids) {
        if (uid in allBakedNodes || (unbakedPcbLayoutNodes && uid in unbakedPcbLayoutNodes)) {
            count++;
        }
    }
    return count;
}

/**
 * Pure function to compute selected PCB layout nodes as an array.
 * Resolves selected PCB node UIDs to actual node objects, checking both
 * baked nodes (preferred) and unbaked nodes (fallback).
 *
 * @param selectedObjectUids The selected object UIDs from the document
 * @param allBakedNodes All baked PCB nodes (from getAllBakedNodes)
 * @param unbakedPcbLayoutNodes The unbaked PCB layout nodes from the document store
 * @returns Array of selected PCB layout nodes
 */
export function computeSelectedPcbLayoutNodes(
    selectedObjectUids: string[] | undefined,
    allBakedNodes: PcbNodesMap<AnyPcbBakedNode>,
    unbakedPcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined,
): AnyPcbBakedNode[] {
    if (!selectedObjectUids) return [];

    // First, filter to only PCB node UIDs (checking both baked and unbaked)
    const selectedPcbNodeUids: string[] = [];
    for (const uid of selectedObjectUids) {
        if (uid in allBakedNodes || (unbakedPcbLayoutNodes && uid in unbakedPcbLayoutNodes)) {
            selectedPcbNodeUids.push(uid);
        }
    }

    // Then, get the actual node objects for each selected UID
    const result: AnyPcbBakedNode[] = [];
    for (const uid of selectedPcbNodeUids) {
        // Check baked nodes first (includes generated nodes like silk text)
        const bakedNode = allBakedNodes[uid];
        if (bakedNode) {
            result.push(bakedNode);
        } else if (unbakedPcbLayoutNodes) {
            // Fall back to unbaked nodes if not found in baked
            const unbakedNode = unbakedPcbLayoutNodes[uid];
            if (unbakedNode) {
                // Cast unbaked node to baked node type since they share the same structure
                result.push(unbakedNode as unknown as AnyPcbBakedNode);
            }
        }
    }
    return result;
}

/**
 * Interface for selection UIDs state.
 * This represents the complete selection state with all selection-related UIDs categorized by type.
 * Includes count properties to avoid computing .length on arrays (performance optimization).
 */
export interface SelectionUidsState {
    all: string[];
    elementUids: string[];
    symbolUids: string[];
    vertexUids: VertexUid[];
    routeUids: string[];
    netUids: string[];
    commentThreadUids: string[];
    pcbLayoutNodeUids: string[];
    pcbLayoutRuleSetUids: string[];
    /**
     * Count properties - use these instead of .length to avoid recomputing array lengths.
     * These are computed once when the state is built, avoiding repeated .length calls.
     */
    allCount: number;
    elementUidsCount: number;
    symbolUidsCount: number;
    vertexUidsCount: number;
    routeUidsCount: number;
    netUidsCount: number;
    commentThreadUidsCount: number;
    pcbLayoutNodeUidsCount: number;
    pcbLayoutRuleSetUidsCount: number;
}

/**
 * @deprecated Use `SelectionUidsState` instead. This alias is kept for backwards compatibility.
 */
export type ISelectionUidsState = SelectionUidsState;

/**
 * Pure function to compute the complete selection UIDs state from document store data.
 * Categorizes selected UIDs into types (elements, symbols, vertices, routes, nets,
 * comment threads, PCB layout nodes, PCB layout rule sets).
 *
 * NOTE: For PCB layout nodes, this checks both baked nodes (from getAllBakedNodes)
 * and unbaked nodes (from document.pcbLayoutNodes) to handle silk text inside components.
 *
 * @param selectedObjectUids The selected object UIDs
 * @param elements The elements map from the document
 * @param nets The nets map from the document
 * @param routeVertices The route vertices map from the document
 * @param routes The routes map from the document
 * @param commentThreads The comment threads map from the document
 * @param unbakedPcbLayoutNodes The PCB layout nodes from the document (unbaked)
 * @param pcbLayoutRuleSets The PCB layout rule sets from the document
 * @param allBakedNodes All baked PCB nodes (from getAllBakedNodes)
 * @returns The complete selection UIDs state
 */
export function computeSelectionUidsState(
    selectedObjectUids: string[],
    elements: IDocumentData["elements"] | undefined,
    nets: INetsMap | undefined,
    routeVertices: IDocumentData["routeVertices"] | undefined,
    routes: IDocumentData["routes"] | undefined,
    commentThreads: IDocumentData["comment_threads"] | undefined,
    unbakedPcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined,
    pcbLayoutRuleSets: IDocumentData["pcbLayoutRuleSets"] | undefined,
    allBakedNodes: {[key: string]: unknown},
): SelectionUidsState {
    // Build element UIDs (matching Redux selectSelectionUidsState behavior - no branch point filtering)
    // Note: Branch point filtering is done separately in elementUids selector
    const elementUidsList: string[] = [];
    for (const uid of selectedObjectUids) {
        if (elements?.[uid]) {
            elementUidsList.push(uid);
        }
    }

    // Build symbol UIDs
    const symbolUidsList: string[] = [];
    for (const uid of selectedObjectUids) {
        const elementUid = ElementHelper.parseSymbolUid(uid)?.elementUid;
        if (elementUid && elements?.[elementUid]) {
            symbolUidsList.push(uid);
        }
    }

    // Build vertex UIDs
    const vertexUidsList: VertexUid[] = [];
    for (const uid of selectedObjectUids) {
        if (routeVertices?.[uid]) {
            vertexUidsList.push(uid as VertexUid);
        }
    }

    // Build route UIDs
    const routeUidsList: string[] = [];
    for (const uid of selectedObjectUids) {
        if (routes?.[uid]) {
            routeUidsList.push(uid);
        }
    }

    // Build net UIDs
    const netUidsList: string[] = [];
    for (const uid of selectedObjectUids) {
        if (nets?.[uid]) {
            netUidsList.push(uid);
        }
    }

    // Build comment thread UIDs
    const commentThreadUidsList: string[] = [];
    for (const uid of selectedObjectUids) {
        if (commentThreads?.[uid]) {
            commentThreadUidsList.push(uid);
        }
    }

    // Build PCB layout node UIDs - check both baked AND unbaked nodes
    // (matching Redux selectSelectionUidsState behavior for silk text inside components)
    const pcbLayoutNodeUidsList: string[] = [];
    for (const uid of selectedObjectUids) {
        if (uid in allBakedNodes || (unbakedPcbLayoutNodes && uid in unbakedPcbLayoutNodes)) {
            pcbLayoutNodeUidsList.push(uid);
        }
    }

    // Build PCB layout rule set UIDs - check both user rulesets AND system rulesets
    // System rulesets are identified by their UID containing the systemDefaultRulesSuffix
    const pcbLayoutRuleSetUidsList: string[] = [];
    for (const uid of selectedObjectUids) {
        if (pcbLayoutRuleSets?.[uid] || uid.includes(systemDefaultRulesSuffix)) {
            pcbLayoutRuleSetUidsList.push(uid);
        }
    }

    return {
        all: selectedObjectUids,
        elementUids: elementUidsList,
        symbolUids: symbolUidsList,
        vertexUids: vertexUidsList,
        routeUids: routeUidsList,
        netUids: netUidsList,
        commentThreadUids: commentThreadUidsList,
        pcbLayoutNodeUids: pcbLayoutNodeUidsList,
        pcbLayoutRuleSetUids: pcbLayoutRuleSetUidsList,
        allCount: selectedObjectUids.length,
        elementUidsCount: elementUidsList.length,
        symbolUidsCount: symbolUidsList.length,
        vertexUidsCount: vertexUidsList.length,
        routeUidsCount: routeUidsList.length,
        netUidsCount: netUidsList.length,
        commentThreadUidsCount: commentThreadUidsList.length,
        pcbLayoutNodeUidsCount: pcbLayoutNodeUidsList.length,
        pcbLayoutRuleSetUidsCount: pcbLayoutRuleSetUidsList.length,
    };
}
